/** Built-in and saved courses, tagged with their source.
 * Callers must clone shared LevelData before giving it to mutable runtime code.
 * Storage and protected-name policy live in storage.ts.
 */

import { loadSavedCourses } from './storage'
import type { LevelData } from '../game/types'
import { COURSE_LIST } from './courses'

/** One playable course, and which shelf it came off. */
export interface CourseEntry {
  name: string
  data: LevelData
  /**
   * False for a course the editor saved. The selector shows the difference, so
   * "mine" and "theirs" are never confused.
   */
  builtIn: boolean
}

/**
 * The saved courses, and the merged list built from them.
 *
 * A saved course is 4800 tile numbers as JSON, so re-parsing it every frame
 * would cost more than drawing the menu does — and the selector asks for the
 * list every frame. Both are dropped by `refreshCourses`, which the shell calls
 * whenever the library can have changed: after the editor closes, and on entry
 * to the select screen. Nothing else writes to storage.
 */
let saved: Record<string, LevelData> | null = null
let merged: CourseEntry[] | null = null

/** Forget what we know about storage, so the next read hits it again. */
export function refreshCourses(): void {
  saved = null
  merged = null
}

function savedCourses(): Record<string, LevelData> {
  if (saved === null) saved = loadSavedCourses()
  return saved
}

function nameKey(name: string): string {
  return name.trim().toLowerCase()
}

/**
 * Everything playable, in play order: the built-ins in their shipped order,
 * then the user's courses by name.
 *
 * The returned array is memoised and shared, so treat it as read-only. A stored
 * course that claims a built-in's name is dropped rather than shown as a second
 * row with the same label: `resolveSaveName` cannot produce one, so this only
 * catches hand-edited storage and courses saved by a build from before the
 * naming policy existed — and in that case the shipped course keeps the name,
 * because it is the one that is guaranteed to be playable.
 */
export function listCourses(): CourseEntry[] {
  if (merged !== null) return merged
  const out: CourseEntry[] = []
  const claimed = new Set<string>()
  for (const data of COURSE_LIST) {
    claimed.add(nameKey(data.name))
    out.push({ name: data.name, data, builtIn: true })
  }
  const user = Object.values(savedCourses())
  // Sorted so the selector is stable between reloads: `localStorage` preserves
  // insertion order, which would otherwise shuffle the list on every save.
  user.sort((a, b) => {
    const ka = nameKey(a.name)
    const kb = nameKey(b.name)
    if (ka === kb) return 0
    return ka < kb ? -1 : 1
  })
  for (const data of user) {
    if (claimed.has(nameKey(data.name))) continue
    claimed.add(nameKey(data.name))
    out.push({ name: data.name, data, builtIn: false })
  }
  merged = out
  return out
}

/** A course by name, built-ins first, or null. Case-insensitive. */
export function courseByName(name: string): CourseEntry | null {
  const wanted = nameKey(name)
  for (const entry of listCourses()) {
    if (nameKey(entry.name) === wanted) return entry
  }
  return null
}
